import { useState, useEffect } from 'react';
import { ShieldAlert, X, ShieldCheck } from 'lucide-react';
import { api } from '../../api/client';
import OAuthRecoveryStep from './OAuthRecoveryStep';
import { useApp } from '../../context/AppContext';

/**
 * OAuthRecoveryBanner
 *
 * Shown to users who haven't linked a Google/Microsoft recovery account yet.
 * - If `require_oauth_recovery` is enabled in settings: the app is blocked until they link.
 * - Otherwise: shows a dismissible banner each login until they link.
 */
export default function OAuthRecoveryBanner() {
    const { currentUser, settings, myKeys } = useApp();
    const [dismissed, setDismissed] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [oauthAvailable, setOauthAvailable] = useState(false);

    const required = !!settings?.require_oauth_recovery;
    const hasLinked = !!(currentUser as any)?.enc_private_key_passkey;

    useEffect(() => {
        // Only bother checking OAuth config if the user hasn't linked yet
        if (hasLinked) return;
        api.getOAuthConfig()
            .then(cfg => setOauthAvailable(!!(cfg.google_client_id || cfg.microsoft_client_id)))
            .catch(() => setOauthAvailable(false));
    }, [hasLinked]);

    // Don't show if user already linked, no OAuth configured, or no keys (still setting up)
    if (hasLinked || !oauthAvailable || !myKeys) return null;

    // Non-required: don't show if dismissed this session
    if (!required && dismissed) return null;

    const privateKey = myKeys.privateKey;

    if (showModal) {
        return (
            <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
                zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
            }}>
                <div style={{
                    background: '#1e293b', borderRadius: 16,
                    padding: 32, maxWidth: 420, width: '100%',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
                    border: '1px solid #334155'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                        <ShieldCheck size={28} style={{ color: '#3b82f6', flexShrink: 0 }} />
                        <div>
                            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Link a Recovery Account</h2>
                            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8' }}>
                                Protect your encrypted messages if you forget your password.
                            </p>
                        </div>
                    </div>
                    {/* Wrap the step in a light/dark wrapper if needed, or rely on its own styles */}
                    <div className="auth-step-wrapper" style={{ color: '#f8fafc' }}>
                        <OAuthRecoveryStep
                            privateKey={privateKey}
                            allowSkip={!required}
                            onSuccess={() => {
                                setShowModal(false);
                                setDismissed(true);
                            }}
                        />
                    </div>
                    {!required && (
                        <button
                            onClick={() => { setShowModal(false); setDismissed(true); }}
                            style={{
                                marginTop: 16, width: '100%', background: '#334155', border: '1px solid #475569',
                                cursor: 'pointer', color: '#f8fafc', fontSize: 13, padding: '10px 0',
                                borderRadius: 8, fontWeight: 500
                            }}
                        >
                            Skip for now — I'll link later
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Blocking overlay (required + not linked)
    if (required) {
        return (
            <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
                zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
            }}>
                <div style={{
                    background: 'var(--bg-secondary)', borderRadius: 16,
                    padding: 32, maxWidth: 400, width: '100%',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
                    textAlign: 'center',
                }}>
                    <ShieldAlert size={40} style={{ color: '#f59e0b', marginBottom: 12 }} />
                    <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Recovery Account Required</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
                        Your administrator requires you to link a Google or Microsoft account
                        before accessing the app. This protects your encrypted messages.
                    </p>
                    <button
                        onClick={() => setShowModal(true)}
                        style={{
                            background: '#3b82f6', color: '#fff', border: 'none',
                            borderRadius: 8, padding: '12px 24px', fontSize: 15,
                            fontWeight: 600, cursor: 'pointer', width: '100%',
                        }}
                    >
                        Link Recovery Account
                    </button>
                </div>
            </div>
        );
    }

    // Non-blocking banner
    return (
        <div style={{
            position: 'fixed', bottom: 20, right: 20, zIndex: 9000,
            background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
            borderRadius: 12, padding: '12px 16px', maxWidth: 340,
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
            <ShieldAlert size={20} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600 }}>
                    Link a recovery account
                </p>
                <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--text-muted)' }}>
                    Protect your encrypted messages in case you forget your password.
                </p>
                <button
                    onClick={() => setShowModal(true)}
                    style={{
                        background: '#3b82f6', color: '#fff', border: 'none',
                        borderRadius: 6, padding: '6px 14px', fontSize: 12,
                        fontWeight: 600, cursor: 'pointer',
                    }}
                >
                    Link Now
                </button>
            </div>
            <button
                onClick={() => setDismissed(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}
                title="Dismiss"
            >
                <X size={16} />
            </button>
        </div>
    );
}
